/**
 * Role definitions and helper functions for access control
 */

// Access to General Report Only but can't approve or reject or view the report
export const AMILA_ROLES_TO_SEE_REPORT = [
  "Ta'leem Secretary",
  "Tabligh Secretary",
  "Tahrik-i-Jadid Secretary",
  "TajneedOfficer-Circuit",
  "TajneedOfficer-National",
  "Tajnid Secretary",
  "Talimul Qur'an Secretary",
  "Tarbiyya Secretary",
  "Umur Aama Secretary",
  "Umur Kharijiiyah Secretary",
  "Waqar-e-Amal Secretary",
  "Waqf-i-Jadid Secretary",
  "Wasiyya Secretary",
  "Sanat-o-Tijarat Secretary",
  "Rishta Nata Secretary",
  "Waqfi Nau Secretary",
  "Nau Muba'een Secretary",
  "Isha'at Secretary",
  "Dhiyafat Secretary",
  "Jaidad Secretary",
  "Audio Visual Secretary",
  "Zira'at Secretary"
];

// This roles can see the general report and be able to approve or reject and view the report
export const CIRCUIT_ROLES = [
  "Jamaat President",
  "Jamaat General Sec"
];

// Access to All Admin Settings Only
export const ADMIN_ROLES = [
  "National General Sec",
  "Administrator",
  "Naib Amir",
  "Amir"
];

// Access to General Report Only with ability to reject, view and approve report
export const NON_ADMIN_ROLES = [
  "Circuit General Sec",
  "Circuit President"
];

// Combined roles for different access levels
export const ALL_ROLES = [
  ...AMILA_ROLES_TO_SEE_REPORT,
  ...CIRCUIT_ROLES,
  ...ADMIN_ROLES,
  ...NON_ADMIN_ROLES
];

// Roles that can access report history
export const REPORT_HISTORY_ROLES = [
  ...CIRCUIT_ROLES,
];

// Roles that can access report settings
export const REPORT_SETTINGS_ROLES = [
  ...ADMIN_ROLES
];

// Roles that can access general report (excluding Jamaat President)
export const GENERAL_REPORT_ROLES = [
  ...AMILA_ROLES_TO_SEE_REPORT,
  "Jamaat General Sec", // Only this role from CIRCUIT_ROLES
  ...NON_ADMIN_ROLES,
  ...ADMIN_ROLES, // Added to allow administration access
];

// Roles that can approve/reject reports
export const APPROVE_REJECT_ROLES = [
  ...CIRCUIT_ROLES,
  ...NON_ADMIN_ROLES
];

// Helper to normalize apostrophes and trim whitespace in role strings
function normalizeRole(role) {
  return role.replace(/[’‘]/g, "'").trim();
}

/**
 * Check if user has any of the specified roles
 * @param {string[]} userRoles - Array of user roles
 * @param {string[]} allowedRoles - Array of roles to check against
 * @returns {boolean} - True if user has any of the allowed roles
 */
export const hasAnyRole = (userRoles, allowedRoles) => {
  if (!userRoles || !allowedRoles) return false;
  // Normalize all roles for robust comparison
  const normalizedUserRoles = userRoles.map(normalizeRole);
  const normalizedAllowedRoles = allowedRoles.map(normalizeRole);
  return normalizedAllowedRoles.some(role => {
    if (Array.isArray(role)) {
      return role.some(subRole => normalizedUserRoles.includes(normalizeRole(subRole)));
    }
    return normalizedUserRoles.includes(role);
  });
};

/**
 * Get current user roles from localStorage
 * @returns {string[]} - Array of user roles or empty array if not found
 */
export const getCurrentUserRoles = () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem('user-details'));
    // Normalize roles on retrieval
    return (userDetails?.data?.roles || []).map(r => normalizeRole(r));
  } catch (error) {
    console.error('Error getting user roles:', error);
    return [];
  }
};

/**
 * Check if current user has admin role
 * @returns {boolean} - True if user has admin role
 */
export const isAdmin = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, ADMIN_ROLES);
};

/**
 * Check if current user has circuit role
 * @returns {boolean} - True if user has circuit role
 */
export const isCircuitRole = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, CIRCUIT_ROLES);
};

/**
 * Check if current user has non-admin role
 * @returns {boolean} - True if user has non-admin role
 */
export const isNonAdminRole = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, NON_ADMIN_ROLES);
};

/**
 * Check if current user has any AMILA_ROLES_TO_SEE_REPORT role
 * @returns {boolean} - True if user has any AMILA role
 */
export const isAmilaRole = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, AMILA_ROLES_TO_SEE_REPORT);
};

/**
 * Check if current user can access report history
 * @returns {boolean} - True if user can access report history
 */
export const canAccessReportHistory = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, REPORT_HISTORY_ROLES);
};

/**
 * Check if current user can access report settings
 * @returns {boolean} - True if user can access report settings
 */
export const canAccessReportSettings = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, REPORT_SETTINGS_ROLES);
};

/**
 * Check if current user can access general report
 * @returns {boolean} - True if user can access general report
 */
export const canAccessGeneralReport = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, GENERAL_REPORT_ROLES);
};

/**
 * Check if current user can approve/reject reports
 * @returns {boolean} - True if user can approve/reject reports
 */
export const canApproveRejectReports = () => {
  const userRoles = getCurrentUserRoles();
  return hasAnyRole(userRoles, APPROVE_REJECT_ROLES);
}; 